import { FC, useMemo } from 'react';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@components/ReportSubSection';
import { DividendNextStepsContainer } from '@/pages/CompanyReport/components/Dividend';
import NextDividend from '../NextDividend';
import type { PayoutEntity } from '../DividendPayout';
import type { DividendStatements } from '@/pages/CompanyReport/components/Dividend/redux/selectors';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import track from 'react-tracking';
import {
  COMPANY_CURRENT_PAYOUT,
  COMPANY_DIVIDEND_YIELD_AND_PAYMENT,
  COMPANY_FUTURE_PAYOUT,
} from '@/constants/feedbackSections';
import { ChartType } from '@/constants/downloadSections';
import { getCompanyInfographicDownloadUrl } from '@utilities/urlGenerator';
import {
  DIVIDEND_PAYOUT_CURRENT,
  DIVIDEND_PAYOUT_FUTURE,
  DIVIDEND_STABILITY_GROWTH,
  DIVIDEND_YIELD,
} from '@/constants/anchors';
import { CompanyStatements } from '@/constants/statementIds';
import PageAnchor from '@components/PageAnchor';
import type { AnalysisCheck } from '@company/redux/interface';
import loadable from '@loadable/component';
import IntroductionDeprecated, {
  Metric,
} from '@/pages/CompanyReport/components/Overview/components/IntroductionDeprecated';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import DividendYieldVsMarketTable from '../DividendYieldVsMarketTable';
import StabilityAndGrowthOfPaymentsTable from '../StabilityAndGrowthOfPaymentsTable';
import type { ViewMode } from '@/components/Company/enum';
import { TableWrapper } from './styled';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import { useReportSplitHandler } from '@/pages/CompanyReport/components/ReportSplitHandlerProvider/ReportSplitHandlerProvider';
import Introduction from '@/pages/CompanyReport/components/Introduction';

const BrazeContentCard = loadable(() => import('@components/BrazeContentCard'));

const DividendPayout = loadable(() => import('../DividendPayout'), {
  ssr: false,
});

const DividendYieldVsMarket = loadable(
  () => import('../DividendYieldVsMarket'),
  { ssr: false }
);

const StabilityAndGrowthOfPaymentsContainer = loadable(
  () => import('../StabilityAndGrowthOfPayments'),
  { ssr: false }
);

export interface Props {
  isFund: boolean;
  isBank: boolean;
  companyId: string;
  companyName: string;
  sectionNumber?: number;
  dividendYield: number | null;
  dividendYieldFuture: number | null;
  dividendNextDate: number;
  companyNamePlural: string;
  showUpcomingDividendPayment: boolean;
  statements: DividendStatements;
  dividendNextPayDate: number;
  keyMetric: string;
  keyMetricTitle: string;
  dividendCheck: AnalysisCheck[];
  industryName: string;
  marketName: string;
  marketDividendYieldP25: number;
  marketDividendYieldP75: number;
  industryDividendYield: number;
  dividendYieldFormatted: string;
  marketDividendYieldP25Formatted: string;
  marketDividendYieldP75Formatted: string;
  industryDividendYieldFormatted: string;
  dividendYieldFutureFormatted: string;
  currentDividendPayout: PayoutEntity[];
  currentDividendPayoutRatio: number;
  currentDividendPayoutRatioFormatted: string;
  futureDividendPayout: PayoutEntity[];
  futureDividendPayoutRatio: number;
  futureDividendPayoutRatioFormatted: string;
  cashDividendPayout: PayoutEntity[];
  cashDividendPayoutRatio: number;
  cashDividendPayoutRatioFormatted: string;
  viewMode: ViewMode;
}

const Dividend: FC<Props> = (props) => {
  const {
    isFund,
    isBank,
    companyId,
    companyName,
    sectionNumber,
    dividendYield,
    dividendYieldFuture,
    marketDividendYieldP25,
    marketDividendYieldP75,
    industryDividendYield,
    statements: {
      isDividendSignificant,
      isDividendYieldTopTier,
      isDividendStable,
      isDividendGrowing,
      isDividendCovered,
      isDividendCoveredIn3Years,
      isDividendCoveredByFreeCashFlow,
    },
    dividendNextDate,
    companyNamePlural,
    dividendNextPayDate,
    keyMetric,
    keyMetricTitle,
    showUpcomingDividendPayment,
    dividendCheck,
    industryName,
    marketName,
    dividendYieldFormatted,
    marketDividendYieldP25Formatted,
    marketDividendYieldP75Formatted,
    industryDividendYieldFormatted,
    dividendYieldFutureFormatted,
    currentDividendPayout,
    currentDividendPayoutRatio,
    currentDividendPayoutRatioFormatted,
    futureDividendPayout,
    futureDividendPayoutRatio,
    futureDividendPayoutRatioFormatted,
    cashDividendPayout,
    cashDividendPayoutRatio,
    cashDividendPayoutRatioFormatted,
    viewMode,
  } = props;

  const {
    dividendYieldDownloadUrl,
    currentPayoutDownloadUrl,
    futurePayoutDownloadUrl,
  } = useMemo(() => {
    return {
      dividendYieldDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.HistoricDividend
      ),
      currentPayoutDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.CurrentDividendPayout
      ),
      futurePayoutDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.FutureDividendPayout
      ),
    };
  }, [companyId]);

  const dateIn3Y = (new Date().getUTCFullYear() + 3).toString();
  const { showFullReport } = useReportSplitHandler();
  const subSectionTitleElement = showFullReport ? 'h3' : 'h2';

  return (
    <ReportSection sectionNumber={sectionNumber} data-cy-id="dividend-section">
      {(SWS_ENV === 'local' || SWS_ENV === 'dev' || SWS_ENV === 'staging') &&
      TEST_RUNNER !== 'cypress' ? (
        <>
          <CompanySectionTitle section="dividend" title="Dividend" />
          <Introduction scoreName="Dividend" />
        </>
      ) : (
        <IntroductionDeprecated
          scoreName="Dividend"
          title={<CompanySectionTitle section="dividend" title="Dividend" />}
          description={`What is ${companyNamePlural} current dividend yield, its reliability and sustainability? `}
          checks={dividendCheck}
          section="dividend"
        >
          <Metric metric={keyMetric} description={keyMetricTitle} />
        </IntroductionDeprecated>
      )}
      {showUpcomingDividendPayment && (
        <>
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Upcoming Dividend Payment"
            trackingProps={{ subject: 'chart' }}
            body={
              <NextDividend
                dividendNextDate={dividendNextDate}
                dividendNextPayDate={dividendNextPayDate}
              />
            }
          />
          <hr />
        </>
      )}

      <BrazeContentCardWrapper>
        <BrazeContentCard
          feedType="company-overview.dividend"
          anchorTarget="_blank"
        />
      </BrazeContentCardWrapper>

      <PageAnchor type="sub-section-title" id={DIVIDEND_YIELD} />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title="Dividend Yield vs Market"
        trackingProps={{ subject: 'chart' }}
        body={
          <ChartWrapper
            id="dividend-yield-vs-market-chart"
            chart={
              <DividendYieldVsMarket
                companyName={companyName}
                industryName={industryName}
                marketName={marketName}
                dividendYield={dividendYield}
                dividendYieldFuture={dividendYieldFuture}
                marketDividendYieldP25={marketDividendYieldP25}
                marketDividendYieldP75={marketDividendYieldP75}
                industryDividendYield={industryDividendYield}
                dividendYieldFormatted={dividendYieldFormatted}
                marketDividendYieldP25Formatted={
                  marketDividendYieldP25Formatted
                }
                marketDividendYieldP75Formatted={
                  marketDividendYieldP75Formatted
                }
                industryDividendYieldFormatted={industryDividendYieldFormatted}
                dividendYieldFutureFormatted={dividendYieldFutureFormatted}
              />
            }
            table={<DividendYieldVsMarketTable />}
            preRenderHeight={318}
          />
        }
        statements={
          <>
            <Statement
              title={isDividendSignificant.title}
              description={<Description statement={isDividendSignificant} />}
              value={isDividendSignificant.value}
              statementId={CompanyStatements.dividendStatement1}
            />
            <Statement
              title={isDividendYieldTopTier.title}
              description={<Description statement={isDividendYieldTopTier} />}
              value={isDividendYieldTopTier.value}
              statementId={CompanyStatements.dividendStatement2}
            />
          </>
        }
      />
      <hr />

      <PageAnchor type="sub-section-title" id={DIVIDEND_STABILITY_GROWTH} />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title="Stability and Growth of Payments"
        trackingProps={{ subject: 'chart' }}
        body={
          <ChartWrapper
            id="dividend-stability-and-growth-of-payments-chart"
            chart={<StabilityAndGrowthOfPaymentsContainer />}
            table={
              <TableWrapper>
                <StabilityAndGrowthOfPaymentsTable
                  viewMode={viewMode}
                  companyName={companyName}
                />
              </TableWrapper>
            }
            feedbackLabel={COMPANY_DIVIDEND_YIELD_AND_PAYMENT}
            exportDarkUrl={dividendYieldDownloadUrl}
          />
        }
        statements={
          <>
            <Statement
              title={isDividendStable.title}
              description={<Description statement={isDividendStable} />}
              value={isDividendStable.value}
              statementId={CompanyStatements.dividendStatement3}
            />
            <Statement
              title={isDividendGrowing.title}
              description={<Description statement={isDividendGrowing} />}
              value={isDividendGrowing.value}
              statementId={CompanyStatements.dividendStatement4}
            />
          </>
        }
      />
      <PageAnchor type="sub-section-title" id={DIVIDEND_PAYOUT_CURRENT} />
      <hr />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title={`${isBank ? 'Current' : 'Earnings'} Payout to Shareholders`}
        trackingProps={{ subject: 'chart' }}
        split={true}
        body={
          <ChartWrapper
            id="dividend-earnings-payout-chart"
            preRenderHeight="258px"
            chart={
              <DividendPayout
                data-cy-id="chart-payout-to-shareholders"
                data={currentDividendPayout}
                payoutType="Earnings"
                payoutRatio={currentDividendPayoutRatio}
                payoutRatioFormatted={currentDividendPayoutRatioFormatted}
                payoutThreshold={0.9} // TODO: Need this supplied by the analysis ideally
                when={isBank ? 'Current' : undefined}
              />
            }
            feedbackLabel={COMPANY_CURRENT_PAYOUT}
            exportDarkUrl={currentPayoutDownloadUrl}
          />
        }
        statements={
          <Statement
            title={isDividendCovered.title}
            description={<Description statement={isDividendCovered} />}
            value={isDividendCovered.value}
            statementId={CompanyStatements.dividendStatement5}
          />
        }
      />
      <PageAnchor type="sub-section-title" id={DIVIDEND_PAYOUT_FUTURE} />
      <hr />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title={`${isBank ? 'Future' : 'Cash'} Payout to Shareholders`}
        trackingProps={{ subject: 'chart' }}
        split={true}
        body={
          <ChartWrapper
            id="dividend-cash-payout-chart"
            preRenderHeight="258px"
            chart={
              <DividendPayout
                data-cy-id="chart-payout-to-shareholders"
                data={isBank ? futureDividendPayout : cashDividendPayout}
                payoutType={isBank ? 'Earnings' : 'Cash flow'}
                payoutRatio={
                  isBank ? futureDividendPayoutRatio : cashDividendPayoutRatio
                }
                payoutRatioFormatted={
                  isBank
                    ? futureDividendPayoutRatioFormatted
                    : cashDividendPayoutRatioFormatted
                }
                payoutThreshold={0.9} // TODO: Need this supplied by the analysis ideally
                payoutRatioIsNegative={
                  isBank
                    ? undefined
                    : 'Paying a dividend with no free cash flows'
                }
                when={isBank ? dateIn3Y : undefined}
              />
            }
            feedbackLabel={COMPANY_FUTURE_PAYOUT}
            exportDarkUrl={isBank ? futurePayoutDownloadUrl : undefined}
          />
        }
        statements={
          isBank ? (
            <Statement
              title={isDividendCoveredIn3Years.title}
              description={
                <Description statement={isDividendCoveredIn3Years} />
              }
              value={isDividendCoveredIn3Years.value}
              statementId={CompanyStatements.dividendStatement6}
            />
          ) : (
            <Statement
              title={isDividendCoveredByFreeCashFlow.title}
              description={
                <Description statement={isDividendCoveredByFreeCashFlow} />
              }
              value={isDividendCoveredByFreeCashFlow.value}
              statementId={CompanyStatements.dividendStatement7}
            />
          )
        }
      />

      {isFund === false && (
        <>
          <hr />
          <DividendNextStepsContainer companyId={companyId} />
        </>
      )}
    </ReportSection>
  );
};

export default track({
  type: 'track',
  subject: 'dividends',
  section: 'dividends',
})(Dividend) as typeof Dividend;
